// Manual / spreadsheet / AI creation page for Work Steps — mirrors
// CreateControlPageV2. The AI branch starts a real collab session (upload
// docs, create session, navigate to the collab wizard) — mirrors
// CreateControlPageV2's onGenerate.
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Progress } from "../../../components/ui/progress";
import {
  FileSpreadsheet,
  FileText,
  Loader2,
  Settings,
  Upload,
  X,
} from "lucide-react";
import { useCreateWorkStep } from "../../../hooks/useWorkSteps";
import { useCreateCollabWorkStepSession } from "../../../hooks/useCollabWorkStep";
import { validateFilesWithToast } from "../../../lib/fileUploadValidation";
import { CONTROL_BUILD_MAX_FILE_SIZE_BYTES } from "../../../lib/fileTypes";
import { WorkStepsUploadCard } from "./import/components/WorkStepsUploadCard";

export const CreateWorkStepPage = () => {
  const navigate = useNavigate();

  // Manual flow
  const { createWorkStep, loading: loadingCreateWorkStep } =
    useCreateWorkStep();

  // AI flow
  const {
    createSession: createCollabSession,
    loading: loadingCollabSession,
    presignedUploadState,
    cancelUpload,
  } = useCreateCollabWorkStepSession();
  const [workStepDefFiles, setWorkStepDefFiles] = useState<File[]>([]);
  const workStepDefInputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedMode, setSelectedMode] = useState<
    "manual" | "spreadsheet" | "ai"
  >("ai");

  const onCreateManually = async () => {
    try {
      const workStep = await createWorkStep({
        name: "New Work Step",
        description: "Work step description",
      });
      navigate(`/audit/work-steps/${workStep.id}`);
    } catch (error) {
      console.error("Failed to create work step:", error);
      toast.error("Failed to create work step");
    }
  };

  const onGenerate = async () => {
    if (workStepDefFiles.length === 0) return;
    const sessionId = await createCollabSession({ files: workStepDefFiles });
    if (sessionId) {
      navigate(`/audit/work-steps/collab/${sessionId}`);
    }
  };

  const removeWorkStepDefFile = (index: number) => {
    setWorkStepDefFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="h-screen overflow-y-auto w-full p-6 max-w-[1100px] mx-auto overflow-x-hidden">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">
          Create a new work step
        </h1>
      </div>

      {/* Setup Manually selectable card */}
      <div
        role="button"
        onClick={() => setSelectedMode("manual")}
        className={`w-full text-left mb-4 transition border rounded-xl p-5 cursor-pointer hover:bg-stone-50 ${
          selectedMode === "manual" ? "ring-2 ring-stone-300" : ""
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <Settings size={20} />
          </div>
          <div>
            <div className="text-lg font-semibold">Setup Manually</div>
            <div className="text-sm text-stone-600">
              Create your work step from scratch with full control over
              procedures and attributes.
            </div>
          </div>
        </div>
      </div>

      {/* Spreadsheet import selectable card — the upload step renders inline
          below; Continue then enters the grouped review. */}
      <div
        role="button"
        onClick={() => setSelectedMode("spreadsheet")}
        className={`w-full text-left mb-4 transition border rounded-xl p-5 cursor-pointer hover:bg-stone-50 ${
          selectedMode === "spreadsheet" ? "ring-2 ring-stone-300" : ""
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <FileSpreadsheet size={20} />
          </div>
          <div>
            <div className="text-lg font-semibold">Import from Spreadsheet</div>
            <div className="text-sm text-stone-600">
              Bulk import work steps from an Excel or CSV export, grouped by
              audit automatically.
            </div>
          </div>
        </div>
      </div>

      {/* AI Generate selectable card */}
      <div
        role="button"
        onClick={() => setSelectedMode("ai")}
        className={`w-full text-left mb-4 border rounded-xl p-5 cursor-pointer hover:bg-stone-50 ${
          selectedMode === "ai" ? "ring-2 ring-stone-300" : ""
        }`}
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <FileText size={20} />
          </div>
          <div>
            <div className="text-lg font-semibold">AI Generate</div>
            <div className="text-sm text-stone-600">
              Generate a work step from your audit program documentation and
              refine it before configuring tests.
            </div>
          </div>
        </div>
      </div>

      {/* Spreadsheet upload step - only when spreadsheet is selected */}
      {selectedMode === "spreadsheet" && <WorkStepsUploadCard />}

      {/* Dropzone - only when AI is selected */}
      {selectedMode === "ai" && (
        <div className="flex flex-col gap-6">
          <Card
            className={`p-6 border-2 border-dashed min-h-[260px] min-w-0 w-full box-border overflow-hidden flex items-center justify-center mt-2 ${
              dragOver ? "bg-stone-50 border-stone-400" : "border-stone-300"
            }`}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragOver(false);
            }}
            onDrop={async (e) => {
              e.preventDefault();
              setDragOver(false);
              const files = Array.from(e.dataTransfer.files || []);
              const validFiles = await validateFilesWithToast(
                files,
                CONTROL_BUILD_MAX_FILE_SIZE_BYTES,
              );
              if (validFiles.length > 0) {
                setWorkStepDefFiles((prev) => [...prev, ...validFiles]);
              }
            }}
          >
            <div className="text-center w-full">
              <div className="flex justify-center mb-4">
                <Upload className="h-10 w-10 text-stone-400" />
              </div>
              <div className="font-medium mb-2">
                Upload the work step detail docs
              </div>
              <div className="max-w-md mx-auto text-left text-xs text-stone-700 bg-stone-100 rounded-md p-3 mb-3">
                <ul className="list-disc pl-4 space-y-1">
                  <li>Audit program or work step documentation</li>
                  <li>Optro work step detail page printed as PDF</li>
                </ul>
              </div>
              {workStepDefFiles.length > 0 ? (
                <div className="mb-3 max-w-[90%] mx-auto space-y-1">
                  {workStepDefFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between gap-2 text-xs text-stone-600 bg-stone-50 px-2 py-1 rounded"
                    >
                      <div className="truncate" title={file.name}>
                        {file.name}
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-stone-200 flex-shrink-0"
                        aria-label="Remove file"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeWorkStepDefFile(index);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
              <Button
                className="gap-2"
                onClick={() => workStepDefInputRef.current?.click()}
                variant="outline"
              >
                <Upload className="h-4 w-4" /> Choose Files
              </Button>
              <Input
                ref={workStepDefInputRef}
                id="workStepDef"
                type="file"
                multiple
                className="hidden"
                onChange={async (e) => {
                  const files = Array.from(e.target.files || []);
                  const validFiles = await validateFilesWithToast(
                    files,
                    CONTROL_BUILD_MAX_FILE_SIZE_BYTES,
                  );
                  if (validFiles.length > 0) {
                    setWorkStepDefFiles((prev) => [...prev, ...validFiles]);
                  }
                }}
              />
            </div>
          </Card>
        </div>
      )}

      {/* Presigned upload progress */}
      {presignedUploadState.isUploading && (
        <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-sm text-stone-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>
                Uploading files... {presignedUploadState.overallProgress}%
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelUpload}
              className="text-xs text-stone-500 hover:text-stone-700"
            >
              Cancel
            </Button>
          </div>
          <Progress
            value={presignedUploadState.overallProgress}
            className="h-2"
          />
          <div className="mt-2 space-y-1">
            {presignedUploadState.files.map((file) => (
              <div
                key={file.filename}
                className="flex items-center justify-between text-xs text-stone-500"
              >
                <span className="truncate max-w-[70%]">{file.filename}</span>
                <span>
                  {file.status === "error"
                    ? file.error || "Failed"
                    : `${file.progress}%`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confirm CTA — spreadsheet mode has its own Continue inside its card */}
      {selectedMode !== "spreadsheet" && (
        <div className="mt-6 flex justify-end">
          <Button
            onClick={selectedMode === "manual" ? onCreateManually : onGenerate}
            disabled={
              loadingCreateWorkStep ||
              loadingCollabSession ||
              presignedUploadState.isUploading ||
              (selectedMode === "ai" && workStepDefFiles.length === 0)
            }
            loading={
              selectedMode === "ai"
                ? loadingCollabSession
                : loadingCreateWorkStep
            }
          >
            {selectedMode === "manual"
              ? "Create Work Step"
              : "Generate Work Step"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CreateWorkStepPage;
